"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useTheme } from "next-themes";
import {
  calendarSeason,
  gardenTheme,
  type GardenMode,
  type GardenSeason,
  type GardenSettings,
  readGardenSettings,
} from "@/lib/garden-atmosphere";

const STORAGE_KEY = "garden-atmosphere-v2";
const LEGACY_STORAGE_KEY = "garden-atmosphere-v1";
interface GardenContextValue {
  ready: boolean;
  settings: GardenSettings;
  minutes: number;
  sceneMinutes: number;
  season: GardenSeason;
  setMode: (mode: GardenMode) => void;
  setMinutes: (minutes: number) => void;
  setSeason: (season: GardenSeason) => void;
}
const GardenContext = createContext<GardenContextValue | null>(null);

export function GardenAtmosphereProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { setTheme, resolvedTheme } = useTheme();
  const [ready, setReady] = useState(false);
  const [now, setNow] = useState<Date | null>(null);
  const [settings, setSettings] = useState<GardenSettings>({
    mode: "system",
    minutes: 720,
    season: "spring",
    manualVisited: false,
  });

  useEffect(() => {
    const read = () => {
      try {
        return readGardenSettings(
          localStorage.getItem(STORAGE_KEY) ??
            localStorage.getItem(LEGACY_STORAGE_KEY),
        );
      } catch {
        return readGardenSettings(null);
      }
    };
    const frame = requestAnimationFrame(() => {
      const stored = read();
      setSettings(stored);
      setNow(new Date());
      setReady(true);
    });
    const tick = () => setNow(new Date());
    const sync = (event: StorageEvent) => {
      if (
        event.key === STORAGE_KEY ||
        event.key === LEGACY_STORAGE_KEY ||
        event.key === null
      ) {
        const stored = read();
        setSettings(stored);
      }
    };
    const interval = window.setInterval(tick, 30_000);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("storage", sync);
    return () => {
      cancelAnimationFrame(frame);
      clearInterval(interval);
      window.removeEventListener("focus", tick);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const minutes =
    settings.mode === "manual"
      ? settings.minutes
      : now
        ? now.getHours() * 60 + now.getMinutes()
        : 720;
  const season =
    settings.mode === "manual"
      ? settings.season
      : now
        ? calendarSeason(now)
        : "spring";
  const theme = gardenTheme(settings.mode, minutes);
  const sceneMinutes =
    settings.mode === "system" ? (resolvedTheme === "dark" ? 0 : 720) : minutes;
  useEffect(() => {
    if (ready) setTheme(theme);
  }, [ready, theme, setTheme]);

  function update(next: GardenSettings) {
    setSettings(next);
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ version: 2, ...next }),
      );
    } catch {
      /* Controls also work when browser storage is unavailable. */
    }
  }
  function setMode(mode: GardenMode) {
    update({
      ...settings,
      mode,
      ...(mode === "manual" && !settings.manualVisited
        ? { minutes, season }
        : {}),
      manualVisited: settings.manualVisited || mode === "manual",
    });
  }
  return (
    <GardenContext.Provider
      value={{
        ready,
        settings,
        minutes,
        sceneMinutes,
        season,
        setMode,
        setMinutes: (value) =>
          update({ ...settings, mode: "manual", minutes: value }),
        setSeason: (value) =>
          update({ ...settings, mode: "manual", season: value }),
      }}
    >
      {children}
    </GardenContext.Provider>
  );
}

export function useGardenAtmosphere() {
  const context = useContext(GardenContext);
  if (!context)
    throw new Error("Garden atmosphere requires GardenAtmosphereProvider");
  return context;
}
